"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import Magnetic from "./ui/Magnetic";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/projects", label: "Projects" },
  { href: "/architecture", label: "Architecture" },
  { href: "/playground", label: "Lab" },
  { href: "/blog", label: "Blog" }
];

const socials = [
  {
    href: "https://github.com/CodeByAmrit",
    iconClass: "fi fi-brands-github",
    label: "GitHub",
    color: "group-hover:text-white",
  },
  {
    href: "https://www.linkedin.com/in/amrit-sharma-b11b88124",
    iconClass: "fi fi-brands-linkedin",
    label: "LinkedIn",
    color: "group-hover:text-blue-400",
  },
  {
    href: "mailto:contact@amritsharma.dev?subject=Project%20Inquiry%20for%20Amrit%20Sharma",
    iconClass: "fi fi-rr-envelope",
    label: "Email",
    color: "group-hover:text-indigo-300",
  },
];

export default function Footer() {
  return (
    <footer className="relative z-10 border-t border-white/5 bg-black/20 backdrop-blur-3xl overflow-hidden font-mono">
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        className="max-w-7xl mx-auto px-6 py-20"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
          {/* Logo and Pitch */}
          <div className="md:col-span-12 lg:col-span-5">
            <Magnetic>
              <Link href="/" className="flex items-center gap-3 mb-6 group select-none">
                <div className="relative w-10 h-10 transition-transform duration-500 group-hover:scale-110">
                  <Image src="/logo.svg" alt="Logo" fill className="object-contain logo-glow" />
                </div>
                <span className="text-white font-black text-xl tracking-tighter font-[var(--font-poppins)]">
                  AMRIT<span className="text-indigo-400">.</span>
                </span>
              </Link>
            </Magnetic>
            <p className="text-gray-400 text-sm leading-relaxed max-w-sm font-medium">
              Architecting high-performance digital ecosystems with a focus on technical purity and visionary software designs.
            </p>
            <div className="mt-8 space-y-2 text-xs font-medium text-gray-400">
              <p>Email: contact@amritsharma.dev</p>
              <p>Location: Sonipat, Haryana, India</p>
              <p>Response Latency: &lt; 2 hours average</p>
            </div>
          </div>

          {/* Diagnostics parameters */}
          <div className="md:col-span-6 lg:col-span-4 space-y-4">
            <h4 className="text-white font-black text-xs tracking-[0.3em] uppercase opacity-40 mb-4">
              Diagnostic Logs
            </h4>
            <div className="space-y-3 text-xs text-gray-400 font-medium">
              <div>
                <span className="text-indigo-400 font-bold uppercase block text-[10px] tracking-wider mb-0.5">Current Tech Stack</span>
                <p>Next.js 16 • React 19 • Tailwind 4 • Docker</p>
              </div>
              <div>
                <span className="text-indigo-400 font-bold uppercase block text-[10px] tracking-wider mb-0.5">Now Building</span>
                <p>High-concurrency user simulation testing suite</p>
              </div>
              <div>
                <span className="text-indigo-400 font-bold uppercase block text-[10px] tracking-wider mb-0.5">Current Reading</span>
                <p>&ldquo;Designing Data-Intensive Applications&rdquo;</p>
              </div>
              <div>
                <span className="text-indigo-400 font-bold uppercase block text-[10px] tracking-wider mb-0.5">Current Learning</span>
                <p>Rust WebAssembly execution profiling</p>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="md:col-span-6 lg:col-span-3">
            <h4 className="text-white font-black text-xs tracking-[0.3em] uppercase opacity-40 mb-6">
              Network Routing
            </h4>
            <ul className="space-y-3 text-xs">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-300 font-bold"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="/profile.html"
                  target="_blank"
                  className="text-indigo-400 hover:text-white transition-colors duration-300 font-black uppercase tracking-widest"
                >
                  View Dossier
                </a>
              </li>
            </ul>

            <div className="flex gap-3 mt-8">
              {socials.map((social) => (
                <Magnetic key={social.label}>
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 rounded-xl glass border border-white/5 flex items-center justify-center text-gray-400 transition-all duration-500 group hover:border-indigo-500/40"
                  >
                    <i className={`${social.iconClass} text-sm transition-colors duration-500 ${social.color}`} />
                  </a>
                </Magnetic>
              ))}
            </div>
          </div>
        </div>

        {/* Footer bottom */}
        <div className="border-t border-white/5 mt-16 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-[10px] text-gray-500 font-medium">
          <p>© {new Date().getFullYear()} Amrit Sharma. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="/sitemap.xml" className="hover:text-white transition-colors">Sitemap</a>
            <a href="/robots.txt" className="hover:text-white transition-colors">Robots.txt</a>
          </div>
        </div>
      </motion.div>
    </footer>
  );
}
